import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { DocumentLinks } from '../components/DocumentLinks';
import { useSiteContent } from '../hooks/useSiteContent';
import { useLanguage } from '../i18n/LanguageContext';
import { mediaUrl } from '../services/api';
import newsHeroImage from '../components/assets/1.jpg';
import iconLinkedin from '../components/assets/icon-linkedin.png';
import iconInstagram from '../components/assets/icon-instagram.svg';

function formatDate(value: string | undefined, uiLocale: string) {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleDateString(uiLocale, { day: '2-digit', month: 'long', year: 'numeric' });
}

export function NewsPage() {
  const { content } = useSiteContent();
  const { t, uiLocale } = useLanguage();
  const [category, setCategory] = useState('all');
  const news = content.news;
  // Platform adi tr-TR ile kucultulmez: "Instagram" -> "ınstagram", "LinkedIn" -> "linkedın" olur
  // ve eslesme bozulur.
  const findSocialUrl = (platform: string) =>
    content.settings.socialLinks?.find((item) => item.platform.trim().toLowerCase() === platform)?.url;
  const linkedinUrl = findSocialUrl('linkedin') || 'https://linkedin.com';
  const instagramUrl = findSocialUrl('instagram') || 'https://instagram.com';

  const categories = useMemo(() => {
    const set = new Set<string>();
    news.forEach((n) => n.category && set.add(n.category));
    return Array.from(set);
  }, [news]);

  const publicationPeriod = useMemo(() => {
    const years = news
      .map((item) => {
        const date = item.publishedAt ? new Date(item.publishedAt) : null;
        return date && !Number.isNaN(date.getTime()) ? date.getFullYear() : null;
      })
      .filter((year): year is number => year !== null)
      .sort((a, b) => a - b);

    if (!years.length) return '2025-2026';
    const first = years[0];
    const last = years[years.length - 1];
    return first === last ? `${first}` : `${first}-${last}`;
  }, [news]);

  const filtered = news.filter(
    (n) => category === 'all' || n.category === category,
  );
  // Sadece portalda "öne çıkan" işaretli haber büyük alana düşer;
  // yeni eklenen haber varsayılan olarak kart grid'inde kalır.
  const featured =
    category === 'all' ? filtered.find((n) => n.isFeatured) : undefined;
  const rest = filtered.filter((n) => n.id !== featured?.id);

  function newsDocuments(item: (typeof news)[number]) {
    const cover = mediaUrl(item.imageUrl);
    return (item.documents || []).filter((doc) => {
      const url = mediaUrl(doc.fileUrl);
      const isImage =
        Boolean(doc.icon?.startsWith('image/')) ||
        Boolean(doc.category?.startsWith('image/')) ||
        /\.(png|jpe?g|gif|webp|svg)$/i.test(doc.title || '');
      // Haber kartlarında resim dosya adlarini belge rozeti olarak gosterme
      if (isImage) return false;
      // Kapak olarak kullanılan görselleri belge rozeti olarak gösterme
      if (cover && url && cover === url) return false;
      return true;
    });
  }

  return (
    <>
      <section
        className="news-hero-section"
        style={{ backgroundImage: `url(${newsHeroImage})` }}
      >
        <div className="news-hero-overlay" />
        <div className="container news-hero-inner">
          <div className="eyebrow news-hero-eyebrow">{t('Ticaret Dürüstlük Üzerine Kuruludur')}</div>
          <h1
            className="news-hero-title"
            dangerouslySetInnerHTML={{
              __html: t('TBH Port Adana’dan<br />Güncel <em>Haberler</em>'),
            }}
          />
          <p className="news-hero-description">
            {t(
              "TBH Port Adana'nın operasyon, yatırım ve sertifikasyon güncellemeleri; sektör etkinlikleri ve kurumsal duyurularımız.",
            )}
          </p>
          <div className="news-hero-stats">
            <div>
              <b>{news.length}</b>
              <span>{t('Toplam Haber')}</span>
            </div>
            <div>
              <b>{categories.length}</b>
              <span>{t('Kategori')}</span>
            </div>
            <div>
              <b>{t('Yayın Dönemi')}</b>
              <span>{publicationPeriod}</span>
            </div>
          </div>
          <a
            href={linkedinUrl}
            className="btn btn-red news-hero-button news-social-icon-btn"
            target="_blank"
            rel="noreferrer"
          >
            <img src={iconLinkedin} alt="" />
            {t('LINKEDIN’DE TAKİP ET')}
          </a>
        </div>
      </section>

      <section>
        <div className="container">
          <div className="news-category-bar">
            <label htmlFor="news-category-select">{t('Kategori')}</label>
            <select
              id="news-category-select"
              className="news-category-select"
              value={category}
              onChange={(e) => setCategory(e.target.value)}
            >
              <option value="all">
                {t('Tümü')} ({news.length})
              </option>
              {categories.map((c) => (
                <option key={c} value={c}>
                  {t(c)} ({news.filter((n) => n.category === c).length})
                </option>
              ))}
            </select>
          </div>

          {featured && (
            <div className="news-featured">
              <div
                className="img-fill"
                style={featured.imageUrl ? { backgroundImage: `url(${mediaUrl(featured.imageUrl)})` } : undefined}
              />
              <div className="txt">
                <div className="eyebrow-sm" style={{ color: '#ff8b84' }}>
                  {t(featured.category || 'Öne Çıkan')}
                </div>
                <h2 className="section-title" style={{ color: '#fff' }}>
                  {t(featured.title)}
                </h2>
                <p style={{ color: '#dde2f4', fontSize: 14, marginBottom: 12 }}>
                  {t(featured.summary)}
                </p>
                <DocumentLinks documents={newsDocuments(featured)} className="inline-docs light" />
                <span style={{ color: '#c7cdea', fontSize: 13 }}>
                  {formatDate(featured.publishedAt, uiLocale)}
                </span>
                <Link to={`/haberler/${featured.slug}`} className="readmore news-featured-readmore">
                  {t('Haberi Oku →')}
                </Link>
              </div>
            </div>
          )}

          <div className="news-grid">
            {rest.map((n) => (
              <div className="news-card" key={n.id}>
                <div
                  className="img-fill"
                  style={n.imageUrl ? { backgroundImage: `url(${mediaUrl(n.imageUrl)})` } : undefined}
                />
                <div className="body">
                  {n.category && (
                    <div className="eyebrow-sm" style={{ marginBottom: 6 }}>
                      {t(n.category)}
                    </div>
                  )}
                  <h4>{t(n.title)}</h4>
                  {n.summary && <p>{t(n.summary)}</p>}
                  <DocumentLinks documents={newsDocuments(n)} />
                  <Link to={`/haberler/${n.slug}`} className="readmore">
                    {t('Haberi Oku →')}
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="news-social-section">
        <div className="container news-social-inner">
          <h2
            className="news-social-title"
            dangerouslySetInnerHTML={{
              __html: t(
                'Bizden haberdar olmak için <span>LinkedIn</span><br />ve <span>Instagram</span> hesaplarımızı takip edin',
              ),
            }}
          />
          <div className="news-social-actions">
            <a
              href={linkedinUrl}
              className="btn btn-red news-social-button news-social-icon-btn"
              target="_blank"
              rel="noreferrer"
            >
              <img src={iconLinkedin} alt="" />
              {t('LINKEDIN’DE TAKİP ET')}
            </a>
            <a
              href={instagramUrl}
              className="btn btn-red news-social-button news-social-icon-btn"
              target="_blank"
              rel="noreferrer"
            >
              <img src={iconInstagram} alt="" />
              {t('INSTAGRAM’DA TAKİP ET')}
            </a>
          </div>
        </div>
      </section>
    </>
  );
}
